import ArrowBackIcon from "@mui/icons-material/ArrowBackRounded";
import ArrowFwdIcon from "@mui/icons-material/ArrowForwardRounded";
import { Box, Button, Divider, Step, StepLabel, Stepper } from "@mui/material";
import { useCallback, useState } from "react";
import { useLocation, useNavigate } from "react-router";
import { withErrorHandling } from "@entrilia/shared/api/axiosHelper";
import type { ApiError } from "@entrilia/shared/api/types";
import DataLoadingFailed from "@entrilia/shared/components/DataLoadingFailed";
import InlineLoader from "@entrilia/shared/components/inlineLoader/InlineLoader";
import { useNotificationContext } from "@entrilia/shared/contexts/NotificationContext";
import useFetch from "@entrilia/shared/hooks/useFetch";
import usePageTitle from "@entrilia/shared/hooks/usePageTitle";
import { logError } from "@entrilia/shared/logging";
import adminApi from "../../../../api/adminApi";
import useClientContext from "../../../../context/useClientContext";
import { pageRoutes } from "../../../../routes";
import { getReferrerPath } from "../../../../utilities/navigationHelper";
import GeneralPageFooter from "../../../common/GeneralPageFooter";
import GeneralPageHeader from "../../../common/GeneralPageHeader";
import DeleteDataImportDialog from "../DeleteDataImportDialog";
import StartImportConfirmationDialog from "../StartImportConfirmationDialog";
import type { DataImportResult } from "../dataImportOperations";
import { useApplyDataImport, useCreateDataImport, useValidateDataImport } from "../dataImportOperations";
import type { OperationStatus, StartDataImportOptions } from "../importDataPagesTypes";
import { isImportDeletable } from "../importDataStateHelper";
import { ImportCsvDataContextProvider } from "./ImportCsvDataContextProvider";
import ImportCsvDataTargetSelectionStep from "./data-selection/ImportCsvDataTargetSelectionStep";
import ImportCsvDataFilesUploadStep from "./files-upload/ImportCsvDataFilesUploadStep";
import {
  finishCreatingImportAction,
  finishValidateImportAction,
  getCreateDataImportRequest,
  getCurrentStepIndex,
  getInitialState,
  initTargetOptionsAction,
  isNextStepAllowed,
  isPreviousStepAllowed,
  nextStepAction,
  previousStepAction,
  setImportCreationErrorAction,
  stepLabels,
  updateApplyImportStatusAction,
  updateCreateImportStatusAction,
  updateValidateImportStatusAction,
} from "./importCsvDataPageState";
import ReviewImportActions from "./review-import/ReviewImportActions";
import ReviewImportStep from "./review-import/ReviewImportStep";

interface DialogState {
  openDialog?: "start_import" | "delete_import";
  errorsCount?: number;
  deleteRecordsCount?: number;
}

type LocationState = { preSelectedObjectType?: string } | null;

const getDataImportChangesTotals = withErrorHandling(adminApi.getDataImportChangesTotals);
const discardDataImport = withErrorHandling(adminApi.discardDataImport);

const ImportCsvDataPage = () => {
  usePageTitle("Data Import");

  const { clientCode } = useClientContext();
  const location = useLocation();
  const navigate = useNavigate();
  const { sendNotification, sendNotificationError } = useNotificationContext();

  const [state, setState] = useState(() => getInitialState((location.state as LocationState)?.preSelectedObjectType));
  const [dialogState, setDialogState] = useState<DialogState>({});

  const handleCreateStatusUpdate = useCallback(
    (status: OperationStatus) => setState(updateCreateImportStatusAction(status)),
    []
  );

  const handleCreateComplete = useCallback(
    ({ dataImport, dataImportMetadata }: DataImportResult) =>
      setState(finishCreatingImportAction(dataImport, dataImportMetadata)),
    []
  );

  const handleCreateError = useCallback((error: ApiError) => {
    setState(setImportCreationErrorAction(error.message));
    logError(error, "[ImportCsvDataPage] createImport");
  }, []);

  const startCreateDataImport = useCreateDataImport({
    onStatusUpdate: handleCreateStatusUpdate,
    onComplete: handleCreateComplete,
    onError: handleCreateError,
  });

  const handleApplyStatusUpdate = useCallback(
    (status: OperationStatus) => setState(updateApplyImportStatusAction(status)),
    []
  );

  const handleApplyComplete = useCallback(() => {
    sendNotification("Data import has been applied successfully");
    navigate(`/${clientCode}/${pageRoutes.settings}/${pageRoutes.importData}`);
  }, [clientCode, navigate, sendNotification]);

  const handleApplyError = useCallback(
    (error: ApiError) => {
      setState(updateApplyImportStatusAction(undefined));
      sendNotificationError(`Error occurred when applying data import: ${error.message}`);
      logError(error, "[ImportCsvDataPage] applyDataImportWithTimeout");
    },
    [sendNotificationError]
  );

  const startApplyDataImport = useApplyDataImport({
    onStatusUpdate: handleApplyStatusUpdate,
    onComplete: handleApplyComplete,
    onError: handleApplyError,
  });

  const handleValidateStatusUpdate = useCallback(
    (status: OperationStatus) => setState(updateValidateImportStatusAction(status)),
    []
  );

  const handleValidateComplete = useCallback(
    ({ dataImport, dataImportMetadata }: DataImportResult) =>
      setState(finishValidateImportAction(dataImport, dataImportMetadata)),
    []
  );

  const handleValidateError = useCallback(
    (error: ApiError) => {
      setState(updateValidateImportStatusAction(undefined));
      sendNotificationError(error.message);
      logError(error, "[ImportCsvDataPage] validateDataImportWithTimeout");
    },
    [sendNotificationError]
  );

  const startValidateDataImport = useValidateDataImport({
    onStatusUpdate: handleValidateStatusUpdate,
    onComplete: handleValidateComplete,
    onError: handleValidateError,
  });

  const [dataImportOptions, fetchError, { isFetching: isFetchingDataImportOptions }] = useFetch(
    adminApi.getDataImportOptions,
    { onSetData: (payload) => setState(initTargetOptionsAction(payload)) }
  );

  if (fetchError) {
    logError(fetchError, "[ImportCsvDataPage] getDataImportOptions");
    return <DataLoadingFailed title="Could not retrieve available import data targets" />;
  }

  const isLoading = dataImportOptions === undefined || isFetchingDataImportOptions;

  const createImport = () => {
    startCreateDataImport(getCreateDataImportRequest(state));
  };

  const applyImport = (options: StartDataImportOptions) => {
    if (!state.dataImport) return;
    startApplyDataImport(state.dataImport.id, options);
  };

  const validateImport = () => {
    if (!state.dataImport) return;
    startValidateDataImport(state.dataImport.id);
  };

  const handlePreviousClick = () => {
    if (state.currentStep.key === "review" && state.dataImport) {
      discardDataImport(state.dataImport.id);
    }

    setState(previousStepAction());
  };

  const handleNextClick = async () => {
    if (state.currentStep.key === "upload") {
      createImport();
      return;
    }

    if (state.currentStep.key === "review") {
      if (!state.dataImport) {
        return;
      }

      const [resp, totalsFetchError] = await getDataImportChangesTotals(state.dataImport.id);
      if (totalsFetchError) {
        logError(totalsFetchError, "[ImportCsvDataPage] getTotalErrorsCountForAllFiles");
        sendNotificationError(totalsFetchError.message);
        return;
      }

      setDialogState({
        openDialog: "start_import",
        errorsCount: resp.validationErrorsCount,
        deleteRecordsCount: resp.deleteRecordsCount,
      });
      return;
    }

    setState(nextStepAction());
  };

  const handleConfirmImport = (options: StartDataImportOptions) => {
    setDialogState({});
    applyImport(options);
  };

  const handleDeleteImport = () => {
    setDialogState({ openDialog: "delete_import" });
  };

  const handleImportDeleted = () => {
    setDialogState({});
    navigate(`/${clientCode}/${pageRoutes.settings}/${pageRoutes.importData}`);
  };

  const backButtonPath =
    getReferrerPath(location.state) ?? `/${clientCode}/${pageRoutes.investorRelations}/${pageRoutes.importData}`;

  return (
    <ImportCsvDataContextProvider pageState={state} setPageState={setState}>
      <GeneralPageHeader title="Data Import" showDefaultBackButtonTo={backButtonPath}>
        {state.dataImport && state.currentStep.key === "review" && (
          <ReviewImportActions
            dataImport={state.dataImport}
            showDownload={state.dataImport.state !== "Created"}
            showValidate
            showDelete={isImportDeletable(state.dataImport.state)}
            disabled={isLoading || state.validateImportStatus !== undefined || state.applyImportStatus !== undefined}
            onValidate={validateImport}
            onDelete={handleDeleteImport}
          />
        )}
      </GeneralPageHeader>
      <Box sx={{ py: 1.5, px: 3, display: "flex", justifyContent: "center" }}>
        <Stepper activeStep={getCurrentStepIndex(state)}>
          {stepLabels.map((label) => (
            <Step key={label}>
              <StepLabel>{label}</StepLabel>
            </Step>
          ))}
        </Stepper>
      </Box>
      <Divider />
      {isLoading && <InlineLoader />}
      {!isLoading && state.currentStep.key === "data" && <ImportCsvDataTargetSelectionStep />}
      {!isLoading && state.currentStep.key === "upload" && <ImportCsvDataFilesUploadStep />}
      {!isLoading && state.currentStep.key === "review" && <ReviewImportStep />}
      <GeneralPageFooter>
        <Button
          variant="outlined"
          startIcon={<ArrowBackIcon />}
          onClick={handlePreviousClick}
          sx={{ visibility: state.currentStep.previousStepKey ? "visible" : "hidden" }}
          disabled={isLoading || !isPreviousStepAllowed(state)}
        >
          Previous
        </Button>
        <Button
          variant="contained"
          endIcon={state.currentStep.key === "review" ? undefined : <ArrowFwdIcon />}
          onClick={handleNextClick}
          disabled={isLoading || !isNextStepAllowed(state)}
        >
          {state.currentStep.key === "review" ? "Start Import" : "Next"}
        </Button>
      </GeneralPageFooter>
      {dialogState.openDialog === "start_import" && state.dataImport !== undefined && (
        <StartImportConfirmationDialog
          dataImport={state.dataImport}
          onClose={() => setDialogState({})}
          onConfirm={handleConfirmImport}
          errorsCount={dialogState.errorsCount ?? 0}
          deleteRecordsCount={dialogState.deleteRecordsCount ?? 0}
        />
      )}
      {dialogState.openDialog === "delete_import" && state.dataImport !== undefined && (
        <DeleteDataImportDialog
          dataImport={state.dataImport}
          onClose={() => setDialogState({})}
          onConfirm={handleImportDeleted}
        />
      )}
    </ImportCsvDataContextProvider>
  );
};

export default ImportCsvDataPage;
